import { useState, useCallback, useEffect } from 'react';
import useDebounce from 'hooks/use-debounce';
import { fetchProposalFieldValues } from 'utils/proposal-fields-utils';
import EverTrue from 'app';

/**
 * Custom hook for debounced proposal field search
 *
 * @param {string} fieldName - The proposal field to search (e.g., 'primary_proposal_unit')
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns {object} Search state and handlers
 */
export const useProposalFieldsSearch = (fieldName, debounceMs = 300) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounce(query, debounceMs);

  useEffect(() => {
    const performSearch = async (searchQuery) => {
      setLoading(true);
      try {
        const fetchedResults = await fetchProposalFieldValues(fieldName, searchQuery, 50);
        setResults(fetchedResults);
      } catch (error) {
        EverTrue.Alert.error(`Error searching proposal field '${fieldName}':`, error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    performSearch(debouncedQuery);
  }, [debouncedQuery, fieldName]);

  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  return { results, loading, query, handleSearch, clearSearch };
};

/**
 * Hook for searching primary proposal units
 */
export const usePrimaryProposalUnitsSearch = (debounceMs = 300) => {
  return useProposalFieldsSearch('primary_proposal_unit', debounceMs);
};

/**
 * Hook for searching additional proposal categories
 */
export const useAdditionalProposalCategoriesSearch = (debounceMs = 300) => {
  return useProposalFieldsSearch('additional_proposal_category', debounceMs);
};

export default useProposalFieldsSearch;
